import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import { FaNewspaper, FaExternalLinkAlt, FaClock, FaBroadcastTower } from 'react-icons/fa';
import { getCompetitorNews, getCompetitorStats } from '../../services/competitorsService';

const mono = "'JetBrains Mono', 'Consolas', monospace";

const Wrap = styled.section`
  margin-top: 1.25rem;
  padding: 1rem 1.1rem;
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.55rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const Title = styled.h4`
  margin: 0;
  font-size: 0.75rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.45rem;

  svg {
    color: var(--btn-primary);
  }
`;

const Meta = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-left: auto;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Item = styled.li`
  display: flex;
  gap: 0.65rem;
  padding: 0.55rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  background: rgba(36, 52, 77, 0.45);
  transition: border-color 0.18s ease, background 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    background: rgba(0, 180, 216, 0.05);
    transform: translateX(2px);
  }
`;

const ItemIndex = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--btn-primary);
  flex-shrink: 0;
  letter-spacing: 0.08em;
  min-width: 26px;
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ItemTitle = styled.a`
  color: var(--text-primary);
  text-decoration: none;
  font-size: 0.88rem;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;

  &:hover {
    color: var(--btn-primary);
  }

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    opacity: 0.6;
  }
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);

  svg {
    font-size: 0.7rem;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 0.4rem;
  margin-bottom: 0.85rem;
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  background: rgba(36, 52, 77, 0.45);
`;

const StatTileLabel = styled.span`
  font-family: ${mono};
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const StatTileValue = styled.span`
  font-family: ${mono};
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
`;

const StatTileSub = styled.span`
  font-family: ${mono};
  font-size: 0.65rem;
  color: var(--btn-primary);
`;

const SourcesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-bottom: 0.85rem;
`;

const SourceChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.55rem;
  background: rgba(0, 180, 216, 0.06);
  border: 1px solid rgba(0, 180, 216, 0.2);
  border-radius: 3px;
  font-size: 0.72rem;
  color: var(--text-secondary);

  strong {
    font-family: ${mono};
    font-size: 0.66rem;
    color: var(--btn-primary);
  }
`;

const Empty = styled.div`
  padding: 0.9rem 0.6rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.85rem;
`;

const Error = styled(Empty)`
  color: var(--alert-error);
`;

const spin = keyframes`to { transform: rotate(360deg); }`;

const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.85rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: var(--btn-primary);
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  const now = new Date();
  const diffMs = now - d;
  const diffH = diffMs / (1000 * 60 * 60);
  if (diffH < 1) return 'hace <1h';
  if (diffH < 24) return `hace ${Math.round(diffH)}h`;
  const diffD = diffH / 24;
  if (diffD < 7) return `hace ${Math.round(diffD)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
}

const CompetitorNewsWidget = ({ slug, limit = 5 }) => {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!slug) {
      setState({ loading: false, error: null, data: { news: [] } });
      setStats(null);
      return;
    }
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    setStats(null);

    Promise.all([getCompetitorNews(slug, limit), getCompetitorStats(slug).catch(() => null)])
      .then(([newsData, statsData]) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data: newsData });
        if (statsData) setStats(statsData);
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            loading: false,
            error: friendlyError(err, 'Error'),
            data: null,
          });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, limit]);

  const lastScraped = state.data?.competitor?.last_scraped_at;
  const news = state.data?.news || [];
  const s = stats?.stats;
  const sources = stats?.top_sources || [];

  return (
    <Wrap>
      <Header>
        <Title>
          <FaBroadcastTower /> Presencia mediática
        </Title>
        <Meta>
          {lastScraped ? `Actualizado ${formatDate(lastScraped)}` : 'Sin actualizar'}
        </Meta>
      </Header>

      {s && (
        <StatsRow>
          <StatTile>
            <StatTileLabel>Últimos 7d</StatTileLabel>
            <StatTileValue>
              {s.last_7d}
              <StatTileSub>notas</StatTileSub>
            </StatTileValue>
          </StatTile>
          <StatTile>
            <StatTileLabel>Últimos 30d</StatTileLabel>
            <StatTileValue>
              {s.last_30d}
              <StatTileSub>notas</StatTileSub>
            </StatTileValue>
          </StatTile>
          <StatTile>
            <StatTileLabel>Total indexado</StatTileLabel>
            <StatTileValue>{s.total}</StatTileValue>
          </StatTile>
          <StatTile>
            <StatTileLabel>Última nota</StatTileLabel>
            <StatTileValue style={{ fontSize: '0.85rem' }}>
              {s.last_article_at ? formatDate(s.last_article_at) : '—'}
            </StatTileValue>
          </StatTile>
        </StatsRow>
      )}

      {sources.length > 0 && (
        <SourcesRow>
          {sources.map((src) => (
            <SourceChip key={src.source}>
              {src.source} <strong>×{src.count}</strong>
            </SourceChip>
          ))}
        </SourcesRow>
      )}

      <Header style={{ marginBottom: '0.6rem', paddingBottom: '0.4rem' }}>
        <Title style={{ fontSize: '0.7rem' }}>
          <FaNewspaper /> Últimas noticias
        </Title>
      </Header>

      {state.loading && <Loading>Cargando</Loading>}
      {state.error && <Error>No se pudo cargar · {state.error}</Error>}
      {!state.loading && !state.error && news.length === 0 && (
        <Empty>Aún no hay noticias indexadas para este competidor.</Empty>
      )}

      {!state.loading && !state.error && news.length > 0 && (
        <List>
          {news.map((n, i) => (
            <Item key={n.id}>
              <ItemIndex>{String(i + 1).padStart(2, '0')}</ItemIndex>
              <ItemBody>
                <ItemTitle href={n.url} target="_blank" rel="noopener noreferrer">
                  {n.title}
                  <FaExternalLinkAlt size={9} />
                </ItemTitle>
                <ItemMeta>
                  {n.source && <span>{n.source}</span>}
                  {n.source && <span>·</span>}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <FaClock /> {formatDate(n.published_at)}
                  </span>
                </ItemMeta>
              </ItemBody>
            </Item>
          ))}
        </List>
      )}
    </Wrap>
  );
};

export default CompetitorNewsWidget;
